import type { SalePaymentResource, SaleResource } from '@repo/api-client';

/**
 * How a sale was paid for — one mode, or one per part of a split payment
 * (#357) — recorded as the terminal submitted it and never inferred (FR-409).
 *
 * Nothing below restates the union: it is read off `SaleResource`, so a fourth
 * mode added to the document fails to compile in this file rather than rendering
 * as `WALLET` on a manager's screen. `tickets/ticket-kinds.ts` does the same for
 * its own two-kind union and the reasoning is identical.
 *
 * This is the split a drawer is counted against, which is why it is a filter and
 * a column rather than a detail on the record: at cash-up the question is how
 * much of the day was cash, and a list that could not answer it would send
 * somebody to open every sale in turn.
 */
export type SalePaymentMode = SaleResource['paymentMode'];

/**
 * The modes, in the order they are offered.
 *
 * Cash first because it is the one that has to reconcile against a physical
 * drawer, and so the one somebody filtering by mode is usually after.
 */
export const SALE_PAYMENT_MODES: readonly SalePaymentMode[] = ['CASH', 'UPI', 'CARD'];

/**
 * `Record` rather than a function with a default, so exhaustiveness is the type
 * system's problem and not a runtime fallback nobody ever sees.
 *
 * "UPI" and "Card" as a person says them. Not the wire values — `CASH` in a
 * table column is the API's spelling leaking onto a screen, and the three are
 * read side by side down a column where one shouting is one too many.
 */
const MODE_LABELS: Record<SalePaymentMode, string> = {
  CASH: 'Cash',
  UPI: 'UPI',
  CARD: 'Card',
};

export const labelForPaymentMode = (mode: SalePaymentMode): string => MODE_LABELS[mode];

/**
 * **A sale's parts, from a server that sends them or one that does not yet.**
 *
 * `payments` is on every sale since blossom-rest-api#560 — one part for a sale
 * paid one way, two or three for a split — and the generated type says so. But
 * Pages deploys this screen on merge, and **production was still on d567269,
 * before #560, when this was written**: its sales carry no `payments` at all,
 * whatever the type says. Read bare, the field would draw nothing for how such a
 * sale was paid. So every back-office read goes through here, and a sale with
 * no parts reads as the one part #560 itself would give it.
 *
 * Generic because the trace's parts are their own shape, with no reference.
 */
export function paymentsOrOne<Part>(
  payments: readonly Part[] | null | undefined,
  one: Part,
): readonly Part[] {
  return payments === undefined || payments === null || payments.length === 0 ? [one] : payments;
}

/** A sale's or a reprint's parts, through {@link paymentsOrOne}. */
export const salePayments = (
  sale: Pick<SaleResource, 'paymentMode' | 'paymentReference' | 'totalPaise'> & {
    // Optional and nullable here because the server may not send it yet.
    readonly payments?: readonly SalePaymentResource[] | null;
  },
): readonly SalePaymentResource[] =>
  paymentsOrOne(sale.payments, {
    paymentMode: sale.paymentMode,
    amountPaise: sale.totalPaise,
    paymentReference: sale.paymentReference,
  });

/** `Cash`, or `Cash + UPI` for a split — where a row has room for one word. */
export const paymentModesLabel = (parts: readonly SalePaymentResource[]): string =>
  parts.map((part) => labelForPaymentMode(part.paymentMode)).join(' + ');
